// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Button, DatePicker, DatePickerInput } from '@carbon/react';

export interface DateRange {
  from: Date;
  to: Date;
}

interface PastRangeFilterProps {
  /** The range in force, or null for the default window (the last month). */
  range: DateRange | null;
  onChange: (range: DateRange | null) => void;
  /** Latest selectable day; defaults to today. */
  maxDate?: Date;
}

/**
 * Date-range control for the Past recommendations tab (AB#33808). Until both
 * ends are picked the tab keeps its default one-month window; "Reset" returns
 * to it. The range filters on when the plan was created.
 */
export default function PastRangeFilter({ range, onChange, maxDate }: PastRangeFilterProps) {
  const max = maxDate ?? new Date();
  return (
    <div className="hs-past-range" role="group" aria-label="Date range">
      <DatePicker
        datePickerType="range"
        dateFormat="m/d/Y"
        maxDate={max}
        value={range ? [range.from, range.to] : []}
        onChange={(dates: Date[]) => {
          if (dates.length === 2) onChange({ from: dates[0], to: dates[1] });
          // Both inputs emptied: back to the default window, not a stale range.
          else if (dates.length === 0 && range) onChange(null);
        }}
      >
        <DatePickerInput id="past-range-from" labelText="From" placeholder="mm/dd/yyyy" size="sm" />
        <DatePickerInput id="past-range-to" labelText="To" placeholder="mm/dd/yyyy" size="sm" />
      </DatePicker>
      <span className="hs-past-range-hint">
        {range ? 'Showing the selected dates.' : 'Showing the last month.'}
      </span>
      {range && (
        <Button kind="ghost" size="sm" onClick={() => onChange(null)}>
          Reset
        </Button>
      )}
    </div>
  );
}
